import React from "react";
import { useTheme } from "styled-components";
import { scroller } from "react-scroll";

import { ABOUT_US_GLOBAL_LINK } from "@config/links";

import { ANCHOR_IDS } from "@typings/anchorIds";
import { AVAILABLE_COUNTRIES, AVAILABLE_LANGS } from "@typings/globals";

import { CSSGrid } from "@components/Layout/CssGrid";
import {
  BentoBox,
  Props as BentoBoxProps,
} from "@components/DataDisplay/BentoBox";
import { info as navBarInfo } from "@components/Navigation/NavBar";

import { mediaQueriesV2 } from "@ui/themes/default";

import { useTranslate } from "@hooks/useTranslate";

import firstImgMobile from "./assets/mobile/1.1.webp";
import firstImgTablet from "./assets/tablet/1.1.webp";
import firstImgDesktop from "./assets/desktop/1.1.webp";
import secondImgMobile from "./assets/mobile/1.2.webp";
import secondImgTablet from "./assets/tablet/1.2.webp";
import secondImgDesktop from "./assets/desktop/1.2.webp";
import * as Styled from "./Bento8x4_2.styled";
import { Bento8ImageCH } from "./Bento8Image.ch";

const areas = CSSGrid.areas({
  first: BentoBox,
  second: BentoBox,
  third: BentoBox,
});

areas.all(({ first, second, third }, { repeat }) => [
  repeat(first, 12),
  repeat(second, 12),
  repeat(third, 12),
]);

areas.smallTablet(({ first, second, third }, { compose, repeat }) => [
  compose(repeat(first, 8), repeat(second, 4)),
  compose(repeat(first, 8), repeat(third, 4)),
]);

const firstBox: BentoBoxProps = {
  testId: "bento_v1_first_box",
  variant: "large",
  heading: "bento_v1_first_box_heading",
  description: "bento_v1_first_box_description",
  cta: "bento_v1_first_box_cta",
  href: ABOUT_US_GLOBAL_LINK,
  backgroundColor: "blue500",
};

const secondBox: BentoBoxProps = {
  testId: "bento_v1_second_box",
  variant: "small",
  heading: "bento_v1_second_box_heading",
  cta: "bento_v1_second_box_cta",
  href: "https://youtu.be/qStvDjSGuiE?si=Cg5ajnEQmtsnT-O4",
  target: "_blank",
  backgroundColor: "green600",
};

const thirdBox: BentoBoxProps = {
  testId: "bento_v1_third_box",
  variant: "small",
  heading: "bento_v1_third_box_heading",
  cta: "bento_v1_third_box_cta",
  href: `#${ANCHOR_IDS.REVIEWS}`,
  backgroundColor: "green600",
  onClick: event => {
    event.preventDefault();

    scroller.scrollTo(ANCHOR_IDS.REVIEWS, {
      smooth: true,
      offset: -navBarInfo.scrollOffset,
    });
  },
};

const firstBase64 =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAaCAYAAADWm14/AAAAAXNSR0IArs4c6QAABdVJREFUSEutls1uG9kRhc+pe8kmRVMSJVEiPQ68SJCF32K22WUAc+nAyMIIEgwweQK9QYABvFCSARIkmBlwkEUQINt5iABaB6FE/dAy9WNS/OlbFVRTFJSBbWUSN9DsRTf7fnWqzulLfMBjd3dX2u12aDQabO439ePdj/P7Xs/7Hvg+97/d/Tb+Y+M61OsnrNVq2ul0Zvf9/4MBdLvdMBqNSiGEULmqEFOk/cv96e7urr4P4oMALBdX3SjJpYZyNqbllsqt8uQ+Ff5rADO7fZakeVXe8ydPnsThsBobuC7lyEsQBL83n1byKvNp55edN/+3Al4hgHB8/OAWwvvscsu5hCQrpVI2iYqslDSJL1iepTxUw2SYhlcvXryYvwviXgW8ysePH5dr41qcpZmUszJH4xJr9bnkKZdpHmMFqTQvhxiSRFUVjWqkzNMM16E+uXr27Nnofwb4/PO/Z63sTWUpb9JMVJOUNclEEKKGGKLEXPMYVGIuQmGuhMznlsa1Urh8+vOnw2XbvgvyXgXcVoP2oDbKUS1byOaleSQROEdQi0JBkJBHUa+cUaOJJBKGJLRpMh2X1C4mDyavnz9/PnmbCu8F+OKLv9ZXxuO6lkq1ZHlGQdkXy4kAB0mLqyijXw0mJmYOAOMkAG/mQc9tnJ09/+yn598LYG/vbysVvW5QdRUh1qhWVWgmwkhjTA4ABOgNDBBoSoCq4FyIazW7MsiwnOavWmet07cl41sV6Ha75XSStqYsNWJI60bWDVwhNAOkRNPorlBXQhFERLz6osKieswIGxt4SdpZCjaompx0XnQu7p0B7/s/m4OtILoFk03CGhBZhVnNYBUHEFjJJddicROfecLDwcPCcqNMBXhjwIVBXwHh1EyPqps47nQ66S7Efyjgfp+cxU1gth2UzUTZIrFhwBqBB6BVYCjDAeA9F6GZ+Po0jyoqoXPvP2hXIIdUDCA80ZT6aZYdfXcWbgG88t7OcEOYbzHZjgVum6EJcNOo6zQ8AFAFmIGIRe8FAsPiHT77BvfA1NSuQV6a4TVgpxA5Ek39FKRfbRQq3H6kWKTcAcqTaqxH0Y2UrGlAi7QdgNuAbQFcN1idwAqADIZi6otlF6dHs09gbsAUxMgMFzA7E/LEjH0aDlVS31L56Ge/+uRs2QZ2f9/dmKaQKef1kLvcsg0HELTMbIdkE4aGGeogancBbLG4/yysB8xBXAPw5DuH2SsSx0b2YXZgEg4MelQdYdD5dcefA796+dUPcoQMUVd96GDcBrUNZduIFuFtwAaAVSwVAIoZuDNM/sl1gBmBawOuAAwBDEgeK3Aoqr1CBbKflTHAKi58IPmnl90fJZOMUVeDcdMMLQegyUMTa8GwbcAGgbUbgAoWAAvbLQ4H8N3PFMAYwJUBr0kMYHYEyAGhPagceBti1ME0Ti88HfnH337942CSQW3Vp16skP4haQ9Bth0AwCYWAN4CB3jXMTFgLMClA8BwCrEj2ALAyJ6q9NX0tDbFxdPPnk74h72vfxhNslRYzRYAkIcGfFRAGHZg2ASxfgNQfg9AoQAL/y8Alv2H4F+S2Fsq8Op6/fzTT38y5Z9/95dHNksVIdYSi4lvC/BRAQA4yA6ALaAAcCu+SwFvgwP4AHrivTbghETflD2B9pTsRZF+DHq6f7B/7ts1frn35ZZKVsmntiaeflwAKPWRzwFgrTsA9RsAn4G7h9vQve1fvJEBlwTOaDgxonCAK+AAFBxWZhgsd0rsvuw+uLaYRbG1FKzJlNogH/lJM1egBaJJw7oBdRgq4K0L3IZLB/iupwBwBYwOYCfwDAB7Ru0J2MuBw4kMT5e7JO7t7ZVibFUyzddSbk0ytakLgEUbCgXcit4Cz4KbOC5c8E4AFACuAPum2nMFQmIvSew/+8Unp7dB5An+zW++qYwyrJUimsjZhugtABRtElsKNOgAixnwQVzkgEcQFxlwpwUXRQuAEwP7Xj2VPYW4FQ/vJuG/ARuDGEZXk60DAAAAAElFTkSuQmCC";
const secondBase64 =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAaCAYAAADWm14/AAAAAXNSR0IArs4c6QAABTJJREFUSEutl81PXOcZxc953zt3ZhiwDeYbDNjg/B/ddl8WjbJgNZIjRWrVP2C2baWkShRFRE2QWTTSdJ9t/ossaoohgPkwGAzM1733fU77jkprIZOkDnd9r85vzvNxniF+wbO6ujoADA24nlWS4BOVzSUhqCeXe6S9jkfnyZPftEjqJhn+An2s/3m9VpQrA/ChkliemFJnSRDk8sTlvfw87+x0dlqNRsNuHUASv/7T14NWrg6kyMty5ZIUXEisD+BZ6pVC1k4n09by8nK4dYBms+mzg6ymoJoSVcS0ZDLn6Cy4UCi4riu67Wwga9Xr9fzWARqN75LH4y+G2oVq9KqkKPUBzEwUcyWu65W1X9vY5Ucf/bp36wDNRjPtjHaGUKBWwFWdZwpH70ijXA7LukjQqnQqF8u/X+7cOsDa2lpFF7UhJPmQM1YNoSwlzkcHSswtVycxuzxPyxcffrh8eesAzY+b1VYZd1HkQ0hQk1wZAd57SUGZUR1v4bJbrb6u15df3zrA5583B9OQ3fPAHXrUEFRxcok5kyzJPNHJfXEREn82PJyc3jQJ77wHVlebd9N2GEZq9wwcdELVyRJzTpTlhGsLOKfPz6xiJysrK923ufBOAHEHfPnlN/cruRsJ0DChO4IGYCo5OgEhB9kScc6QvFLonKz8buXs1gDiBFzcD6NQMZbIjZjjXUo1I9Mo4qRMZMuZXqPEkyLweOdk8qjR+FVxHeKdHPjqj18N+WptnM7GzTBKaFjkIGipRDoqk7EF8swcjhO5o27JHb6tGd8JYP2L9XEpmVRhk0Y37qQRkEOCyiQoIeuXQDgF7aXkD0zhYO/VzMF1F/5vgDh+l5V8ygVNCZyWMEFGF3AHQkURgMgIXNL0So5HMuwDelFOuP/+k/dP3ywDG42G+7G0ul6z1dW/jVYCps00C9MMnZuCNCbhLh2rsUEJZYK7BOwExCHEPZnbM297bRs+eHM1M/4inCMsN5azn4rmTz/9tjzgTicTaVbkAxgfgJohNG7gPQIDAB2hnoEXBI4BHZDYpbRjwG6Wuv16/bfHV1pc+2TtXjutFUly2vux1IofxHflS9MgHzhiXsC8hFkCExBGQAzELCYYw+ccwDGBF0BffBvmd6ILd7LS0VU+MDZU1qkUaej2NlubnZvKEeO3t9cbz5JkxkPzkC2AXJDwAMQUgREBtTiFAHoEzwUdQdijw7aB2wS2Pd1u1nOHu+ffn0UtPv3i6UzWKxWJer0LP9G5KTrj+VXNqhNBnIVzCwQfSngkao7AdN+B/wHEcp4ROBKwC3ELxCaELXjtAKWDizB4GrX49C9P5wqfFgmzXrfU7dTr9fbbeiGu3kpuE8HCHKGHIB4BXPx3ZeYjgIBRAJX/fBtPsDNBhwR/APAc4iaJTTn7oQjYL/fsNDrOtU++WSh8KH7qiPzrx80Rl2ST9G4OhkeElgxcpLQAYgbA/Wvg8QbYRwSIwrANCJsUtp2FF5el6quxMXS4/tn6w8KVinhEXlil/fLl9+3rfXC1+9PMpig/B9iiqCWBSwQeEpgVMHgNIBDYF7At4J8QntFhA6atK4Co9bMA4q6YmnpvpD//BebhbBHCY4CPI4CA2Tfsf5MjAmxR2ADxTOIGYc8jAHOefPCHD34eQHRg7bO/j/qkmFIICwa3SOA9AktC7IU+QD+Irj2Hsf6ENgT+4wpA3u8N9vxxHMX/OoDguzf9kbgCEHrTPs4//RKkx30X+s3YByi9HUBbBJ5FgL4LcM+Dhb27RXoSAf4F8DgwSyhVkeEAAAAASUVORK5CYII=";

interface Props {
  language: AVAILABLE_LANGS;
  country: AVAILABLE_COUNTRIES;
}

export const Bento8x4_2: React.FC<Props> = props => {
  const theme = useTheme();
  const translate = useTranslate("batmaid_pwa_static_homepage");

  const isCH = props.country === AVAILABLE_COUNTRIES.CH;

  return (
    <CSSGrid
      areas={areas}
      rowGap={{
        default: theme.spacing.spacing_24,
        smallDesktop: theme.spacing.spacing_32,
      }}
      templateRows={{
        default: "auto",
        smallTablet: "1fr 1fr",
      }}
      areasProps={{
        first: {
          ...firstBox,
          heading: translate(firstBox.heading),
          description:
            typeof firstBox.description === "string"
              ? translate(firstBox.description)
              : firstBox.description,
          cta: translate(firstBox.cta!),
          href: `/${props.language}${firstBox.href}`,
          children: (
            <React.Fragment>
              {isCH && <Bento8ImageCH language={props.language} />}
              {!isCH && (
                <BentoBox.Image
                  width={1696}
                  height={1384}
                  src={firstImgMobile}
                  alt={translate("bento_v1_first_box_img_alt")}
                  base64={firstBase64}
                  loading="lazy"
                  sources={
                    <React.Fragment>
                      <source
                        srcSet={firstImgMobile}
                        media={mediaQueriesV2.largeMobile}
                      />
                      <source
                        srcSet={firstImgTablet}
                        media={mediaQueriesV2.smallTablet}
                      />
                      <source
                        srcSet={firstImgTablet}
                        media={mediaQueriesV2.tablet}
                      />
                      <source
                        srcSet={firstImgDesktop}
                        media={mediaQueriesV2.smallDesktop}
                      />
                      <source
                        srcSet={firstImgDesktop}
                        media={mediaQueriesV2.desktop}
                      />
                      <source
                        srcSet={firstImgDesktop}
                        media={mediaQueriesV2.largeDesktop}
                      />
                    </React.Fragment>
                  }
                />
              )}
            </React.Fragment>
          ),
        },
        second: {
          ...secondBox,
          heading: translate(secondBox.heading),
          cta: translate(secondBox.cta!),

          children: (
            <BentoBox.Image
              width={1026}
              height={692}
              src={secondImgMobile}
              alt={translate("bento_v1_second_box_img_alt")}
              base64={secondBase64}
              loading="lazy"
              sources={
                <React.Fragment>
                  <source
                    srcSet={secondImgMobile}
                    media={mediaQueriesV2.largeMobile}
                  />
                  <source
                    srcSet={secondImgTablet}
                    media={mediaQueriesV2.smallTablet}
                  />
                  <source
                    srcSet={secondImgTablet}
                    media={mediaQueriesV2.tablet}
                  />
                  <source
                    srcSet={secondImgDesktop}
                    media={mediaQueriesV2.smallDesktop}
                  />
                  <source
                    srcSet={secondImgDesktop}
                    media={mediaQueriesV2.desktop}
                  />
                  <source
                    srcSet={secondImgDesktop}
                    media={mediaQueriesV2.largeDesktop}
                  />
                </React.Fragment>
              }
            />
          ),
        },
        third: {
          ...thirdBox,
          heading: translate(thirdBox.heading),
          cta: translate(thirdBox.cta!),
          children: (
            <Styled.Rating>
              <Styled.RatingLabel>4.6</Styled.RatingLabel>
              <Styled.StarIcon />
            </Styled.Rating>
          ),
        },
      }}
    />
  );
};
